"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";

/**
 * SponsorMarquee — a logo strip for the featureList `displayStyle:
 * 'sponsors'` variant (um1sf theme).
 *
 * Behaviour:
 *   - Few logos that fit the viewport → a static, centered, wrapping row.
 *   - More logos than fit → a continuous horizontal auto-scroll
 *     (marquee). The track is duplicated so the scroll loops seamlessly.
 *     Hovering pauses the motion so a visitor can click a logo.
 *
 * Each item uses `image` as the logo and an optional `link.url` to make
 * the logo clickable. Logos render greyscale and lift to full colour on
 * hover — standard "our sponsors" treatment.
 */

interface SponsorItem {
  id?: string;
  image?: string;
  title?: string; // already localized by the caller
  link?: { url: string; openInNewTab?: boolean };
}

interface SponsorMarqueeProps {
  items: SponsorItem[];
  /** seconds for one full loop of the track; lower = faster */
  speedSeconds?: number;
}

function Logo({ item }: { item: SponsorItem }) {
  const img = (
    // Plain <img> (not next/image) — sponsor logos come from arbitrary
    // CDN/media URLs that aren't in the next.config image allowlist.
    <img
      src={item.image}
      alt={item.title || "Sponsor"}
      title={item.title}
      loading="lazy"
      className="h-12 md:h-16 w-auto max-w-[180px] object-contain grayscale opacity-70 transition duration-300 hover:grayscale-0 hover:opacity-100"
    />
  );

  const wrapper = "shrink-0 flex items-center justify-center px-6 md:px-10";

  if (item.link?.url) {
    return (
      <Link
        href={item.link.url}
        target={item.link.openInNewTab ? "_blank" : undefined}
        rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
        className={wrapper}
      >
        {img}
      </Link>
    );
  }
  return <div className={wrapper}>{img}</div>;
}

export function SponsorMarquee({
  items,
  speedSeconds = 30,
}: SponsorMarqueeProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // Decide between static row and marquee by measuring whether the
  // natural row width exceeds the viewport. Re-measure on resize.
  useEffect(() => {
    const measure = () => {
      const vp = viewportRef.current;
      const row = rowRef.current;
      if (!vp || !row) return;
      setOverflowing(row.scrollWidth > vp.clientWidth + 4);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [items.length]);

  const logos = items.filter((it) => it.image);
  if (logos.length === 0) return null;

  // Static, centered, wrapping row when everything fits.
  if (!overflowing) {
    return (
      <div ref={viewportRef} className="w-full overflow-hidden">
        <div
          ref={rowRef}
          className="flex flex-wrap items-center justify-center gap-y-6"
        >
          {logos.map((it, idx) => (
            <Logo key={it.id ?? idx} item={it} />
          ))}
        </div>
      </div>
    );
  }

  // Marquee: duplicate the track so the -50% translate loops seamlessly.
  return (
    <div
      ref={viewportRef}
      className="um1sf-sponsor-marquee w-full overflow-hidden"
      // Edge fade so logos appear/vanish softly at the viewport edges.
      style={{
        maskImage:
          "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
      }}
    >
      <div
        className="um1sf-sponsor-track flex w-max items-center"
        style={{ animationDuration: `${speedSeconds}s` }}
      >
        {/* hidden measuring copy uses the same nodes as the first track */}
        <div ref={rowRef} className="flex items-center">
          {logos.map((it, idx) => (
            <Logo key={`a-${it.id ?? idx}`} item={it} />
          ))}
        </div>
        <div className="flex items-center" aria-hidden="true">
          {logos.map((it, idx) => (
            <Logo key={`b-${it.id ?? idx}`} item={it} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes um1sf-sponsor-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .um1sf-sponsor-track {
          animation-name: um1sf-sponsor-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .um1sf-sponsor-marquee:hover .um1sf-sponsor-track {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .um1sf-sponsor-track { animation: none; }
        }
      `}</style>
    </div>
  );
}

export default SponsorMarquee;
